/**
 * Wishlist Assign Popover - "Saved — add to a project?" popover after hearting
 *
 * @ui @wishlist @project-organizer @popover @shopify
 *
 * After a logged-in customer hearts a product on a collection card or the
 * product page, wishlist.js calls window.WishlistAssign.onAdded(product,
 * anchorEl) (added-only, logged-in only — see the hook seam in wishlist.js's
 * heart click handlers). This module renders a small popover anchored near
 * the heart, listing the customer's projects (expandable to folders) so the
 * product can be filed immediately. Dismissing (×, outside click, Escape, or
 * idle) leaves today's behavior intact — the product stays wishlisted either
 * way; this widget is purely additive.
 *
 * Server-only: requires customer login (uses X-Customer-* headers), same
 * convention as wishlist-projects.js. Projects are fetched once per page
 * load and cached in module state; a project create refreshes the cache.
 *
 * Deployed to Shopify theme as assets/wishlist-assign.js.
 *
 * See Also: wishlist.js, wishlist-projects.js
 */

(function () {
  'use strict';

  var API_BASE = '/apps/trade-program/wishlist';
  var PORTAL_URL = 'https://my.surfacegroup.com/portal/projects';
  var IDLE_MS = 12000;
  var SUCCESS_MS = 4000;

  // Module state — only one popover can exist at a time.
  var projectsCache = null;     // Array once fetched; null = not fetched yet
  var projectsPromise = null;   // in-flight GET /projects (de-dupes concurrent opens)
  var popoverEl = null;         // currently open popover element, or null
  var idleTimer = null;
  var successTimer = null;
  var openToken = 0;            // guards stale async renders when re-hearting fast
  var expandedId = null;        // project _id whose chips are showing (accordion), or null
  var newProjectFormOpen = false;

  // ------------------------------------------------------------------
  // API helpers (mirrors wishlist-projects.js's _api / _headers / _esc)
  // ------------------------------------------------------------------

  function _headers() {
    var h = { 'Content-Type': 'application/json' };
    var c = window.__sgiCustomer || {};
    if (c.id) h['X-Customer-Id'] = c.id;
    if (c.email) h['X-Customer-Email'] = c.email;
    return h;
  }

  function _api(method, path, body) {
    var opts = { method: method, headers: _headers() };
    if (body) opts.body = JSON.stringify(body);
    return fetch(API_BASE + path, opts).then(function (r) {
      if (!r.ok) return r.json().then(function (d) { throw new Error(d.detail || 'Request failed'); });
      return r.json();
    });
  }

  function _esc(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  function _loadProjects() {
    if (projectsCache) return Promise.resolve(projectsCache);
    if (projectsPromise) return projectsPromise;
    projectsPromise = _api('GET', '/projects').then(function (data) {
      projectsCache = data.projects || [];
      projectsPromise = null;
      return projectsCache;
    }).catch(function (e) {
      projectsPromise = null;
      throw e;
    });
    return projectsPromise;
  }

  // ------------------------------------------------------------------
  // Styles
  // ------------------------------------------------------------------

  function _injectStyles() {
    if (document.getElementById('wishlist-assign-css')) return;
    var s = document.createElement('style');
    s.id = 'wishlist-assign-css';
    s.textContent = [
      '.wa-popover{position:fixed;z-index:10000;width:300px;max-width:min(320px,calc(100vw - 20px));max-height:380px;overflow-y:auto;background:#fff;border:1px solid #e5e7eb;border-radius:8px;box-shadow:0 6px 20px rgba(0,0,0,.15);font-size:13px;color:#374151;box-sizing:border-box;}',
      '.wa-header{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:12px 14px;border-bottom:1px solid #e5e7eb;}',
      '.wa-header__title{font-size:14px;font-weight:600;color:#1f2937;}',
      '.wa-close{background:none;border:none;font-size:20px;line-height:1;color:#9ca3af;cursor:pointer;padding:0 2px;}',
      '.wa-close:hover{color:#374151;}',
      '.wa-body{padding:10px 14px 14px;}',
      '.wa-explainer{margin:0 0 10px;color:#6b7280;font-size:12.5px;}',
      '.wa-rows{display:flex;flex-direction:column;gap:2px;}',
      '.wa-row{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px;border-radius:6px;cursor:pointer;}',
      '.wa-row:hover,.wa-row--open{background:#f9fafb;}',
      '.wa-row__name{font-weight:500;color:#1f2937;}',
      '.wa-row__count{font-size:11.5px;color:#9ca3af;white-space:nowrap;}',
      '.wa-row--new{color:#9ca3af;font-style:italic;}',
      '.wa-row--new:hover{color:#c9a96e;}',
      '.wa-chips{display:flex;flex-wrap:wrap;gap:6px;padding:4px 8px 10px;}',
      '.wa-chip{padding:5px 12px;font-size:12px;border:1px solid #d1d5db;border-radius:14px;background:#fff;cursor:pointer;color:#374151;}',
      '.wa-chip:hover{border-color:#c9a96e;color:#c9a96e;}',
      '.wa-chip:disabled{opacity:.5;cursor:default;}',
      '.wa-new-form{display:flex;gap:6px;margin-top:8px;}',
      '.wa-input{flex:1;min-width:0;padding:7px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:12.5px;box-sizing:border-box;}',
      '.wa-input:focus{outline:none;border-color:#c9a96e;}',
      '.wa-create-btn{padding:7px 12px;border:none;border-radius:6px;background:#c9a96e;color:#fff;font-size:12.5px;font-weight:500;cursor:pointer;white-space:nowrap;}',
      '.wa-create-btn:disabled{opacity:.6;cursor:default;}',
      '.wa-error{display:none;margin-top:8px;color:#ef4444;font-size:12px;}',
      '.wa-error:not(:empty){display:block;}',
      '.wa-success__text{margin:0 0 8px;color:#1f2937;}',
      '.wa-success__link{color:#c9a96e;font-weight:600;text-decoration:none;font-size:12.5px;}',
      '.wa-success__link:hover{text-decoration:underline;}',
    ].join('\n');
    document.head.appendChild(s);
  }

  // ------------------------------------------------------------------
  // Popover lifecycle
  // ------------------------------------------------------------------

  function onAdded(product, anchorEl) {
    if (!product || !anchorEl) return;
    if (!window.__sgiCustomer || !window.__sgiCustomer.id) return; // logged-in only

    _close(); // never show two at once — re-heart replaces
    var myToken = ++openToken;

    _loadProjects().then(function (projects) {
      if (myToken !== openToken) return; // superseded by a newer heart click
      _render(product, anchorEl, projects);
    }).catch(function (e) {
      console.log('[wishlist-assign] projects fetch failed, skipping popover:', e); // eslint-disable-line no-console
    });
  }

  function _render(product, anchorEl, projects) {
    _injectStyles();
    expandedId = null;
    newProjectFormOpen = projects.length === 0;

    var el = document.createElement('div');
    el.className = 'wa-popover';
    el.style.visibility = 'hidden';
    el.innerHTML = '<div class="wa-header">' +
        '<span class="wa-header__title">Saved ♥ Add to a project?</span>' +
        '<button type="button" class="wa-close" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="wa-body"></div>';
    el.querySelector('.wa-close').addEventListener('click', _close);

    popoverEl = el;
    document.body.appendChild(el);
    _renderBody(el, product, projects);
    _reposition(el, anchorEl);
    el.style.visibility = '';

    // Defer outside-click binding so the heart click that opened this
    // popover doesn't immediately close it.
    setTimeout(function () {
      document.addEventListener('click', _onOutside, true);
      document.addEventListener('touchstart', _onOutside, true);
      document.addEventListener('keydown', _onKeydown);
      window.addEventListener('scroll', _close);
    }, 0);
    _resetIdle();
  }

  function _reposition(el, anchorEl) {
    var margin = 10;
    var gap = 8;
    var rect = anchorEl.getBoundingClientRect();
    var elRect = el.getBoundingClientRect();
    var vw = window.innerWidth;
    var vh = window.innerHeight;

    var left = rect.left; // default: below-right of the heart
    if (left + elRect.width > vw - margin) left = vw - margin - elRect.width;
    if (left < margin) left = margin;

    var top = rect.bottom + gap;
    if (top + elRect.height > vh - margin) {
      var above = rect.top - gap - elRect.height;
      top = above >= margin ? above : Math.max(margin, vh - margin - elRect.height);
    }

    el.style.left = left + 'px';
    el.style.top = top + 'px';
  }

  function _close() {
    if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
    if (successTimer) { clearTimeout(successTimer); successTimer = null; }
    if (popoverEl && popoverEl.parentNode) popoverEl.parentNode.removeChild(popoverEl);
    popoverEl = null;
    expandedId = null;
    newProjectFormOpen = false;
    document.removeEventListener('click', _onOutside, true);
    document.removeEventListener('touchstart', _onOutside, true);
    document.removeEventListener('keydown', _onKeydown);
    window.removeEventListener('scroll', _close);
  }

  // Capture phase: heart click handlers call stopPropagation(), so a
  // bubble-phase document listener would never see clicks on other hearts.
  function _onOutside(e) {
    if (popoverEl && !popoverEl.contains(e.target)) _close();
  }

  function _onKeydown(e) {
    if (e.key === 'Escape') _close();
  }

  function _resetIdle() {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(_close, IDLE_MS);
  }

  // ------------------------------------------------------------------
  // Render: body states (rows / expanded chips / new-project form)
  // ------------------------------------------------------------------

  function _renderBody(el, product, projects) {
    var body = el.querySelector('.wa-body');
    var html = '';

    if (!projects.length) {
      html += '<p class="wa-explainer">You don’t have any projects yet — create one to save this product.</p>';
      html += _newFormHtml();
    } else {
      html += _rowsHtml(projects);
      html += newProjectFormOpen ? _newFormHtml() : _newRowHtml();
    }
    html += '<div class="wa-error"></div>';
    body.innerHTML = html;
    _wireBody(el, product, projects);
  }

  function _rowsHtml(projects) {
    var html = '<div class="wa-rows">';
    for (var i = 0; i < projects.length; i++) {
      var p = projects[i];
      var folders = p.folders || [];
      var isOpen = expandedId === p._id;
      html += '<div class="wa-row' + (isOpen ? ' wa-row--open' : '') + '" data-project-id="' + _esc(p._id) + '">' +
        '<span class="wa-row__name">' + _esc(p.name) + '</span>' +
        '<span class="wa-row__count">' + folders.length + (folders.length === 1 ? ' folder' : ' folders') + '</span>' +
      '</div>';
      if (isOpen) html += _chipsHtml(p);
    }
    html += '</div>';
    return html;
  }

  function _chipsHtml(project) {
    var folders = project.folders || [];
    var hasGeneral = folders.some(function (f) { return (f.name || '').toLowerCase() === 'general'; });
    var html = '<div class="wa-chips">';
    if (!hasGeneral) html += '<button type="button" class="wa-chip" data-kind="general">General</button>';
    for (var j = 0; j < folders.length; j++) {
      html += '<button type="button" class="wa-chip" data-kind="folder" data-folder-id="' + _esc(folders[j].id) + '">' + _esc(folders[j].name) + '</button>';
    }
    html += '</div>';
    return html;
  }

  function _newRowHtml() {
    return '<div class="wa-row wa-row--new" data-action="new-project"><span class="wa-row__name">+ New project…</span></div>';
  }

  function _newFormHtml() {
    return '<div class="wa-new-form">' +
      '<input type="text" class="wa-input" placeholder="e.g. Kitchen Renovation" maxlength="80">' +
      '<button type="button" class="wa-create-btn">Create</button>' +
    '</div>';
  }

  function _wireBody(el, product, projects) {
    var body = el.querySelector('.wa-body');

    body.querySelectorAll('.wa-row[data-project-id]').forEach(function (rowEl) {
      rowEl.addEventListener('click', function () {
        var pid = rowEl.getAttribute('data-project-id');
        expandedId = expandedId === pid ? null : pid;
        newProjectFormOpen = false;
        _renderBody(el, product, projects);
        _resetIdle();
      });
    });

    var newRow = body.querySelector('.wa-row--new');
    if (newRow) {
      newRow.addEventListener('click', function () {
        expandedId = null;
        newProjectFormOpen = true;
        _renderBody(el, product, projects);
        _resetIdle();
      });
    }

    var form = body.querySelector('.wa-new-form');
    if (form) _wireNewForm(form, el, product);

    body.querySelectorAll('.wa-chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        _resetIdle();
        var project = projects.filter(function (p) { return p._id === expandedId; })[0];
        if (!project) return;
        if (chip.getAttribute('data-kind') === 'general') {
          _assign(el, product, project, null);
        } else {
          var fid = chip.getAttribute('data-folder-id');
          var folder = (project.folders || []).filter(function (f) { return String(f.id) === String(fid); })[0];
          _assign(el, product, project, folder || { id: fid, name: 'Folder' });
        }
      });
    });
  }

  function _wireNewForm(form, el, product) {
    var input = form.querySelector('.wa-input');
    var btn = form.querySelector('.wa-create-btn');
    input.focus();
    input.addEventListener('input', _resetIdle);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); btn.click(); }
    });

    btn.addEventListener('click', function () {
      var name = input.value.trim();
      if (!name) return;
      _clearError(el);
      btn.disabled = true;
      _api('POST', '/projects', { name: name }).then(function () {
        projectsCache = null; // invalidate — refresh so the new project's real shape comes back
        return _loadProjects();
      }).then(function (fresh) {
        var created = fresh.filter(function (p) { return p.name === name; }).pop() || fresh[fresh.length - 1];
        newProjectFormOpen = false;
        expandedId = created ? created._id : null;
        _renderBody(el, product, fresh);
      }).catch(function (e) {
        btn.disabled = false;
        _showError(el, e.message);
      });
    });
  }

  // ------------------------------------------------------------------
  // Assign + success / error states
  // ------------------------------------------------------------------

  function _assign(el, product, project, folder) {
    _clearError(el);
    var chips = el.querySelectorAll('.wa-chip');
    chips.forEach(function (c) { c.disabled = true; });

    var path = folder
      ? '/projects/' + project._id + '/folders/' + folder.id + '/products'
      : '/projects/' + project._id + '/products';
    var productId = product.product_id || product.id || '';

    _api('POST', path, { product_id: productId }).then(function () {
      _renderSuccess(el, project, folder);
    }).catch(function (e) {
      chips.forEach(function (c) { c.disabled = false; });
      _showError(el, e.message);
    });
  }

  function _renderSuccess(el, project, folder) {
    if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
    var label = project.name + ' · ' + (folder ? folder.name : 'General');
    var body = el.querySelector('.wa-body');
    body.innerHTML = '<div class="wa-success">' +
      '<p class="wa-success__text">Added to &quot;' + _esc(label) + '&quot;</p>' +
      '<a class="wa-success__link" href="' + PORTAL_URL + '/' + encodeURIComponent(project._id) + '" target="_blank" rel="noopener">View project</a>' +
    '</div>';
    successTimer = setTimeout(_close, SUCCESS_MS);
  }

  function _showError(el, msg) {
    var box = el.querySelector('.wa-error');
    if (box) box.textContent = msg || 'Something went wrong.';
  }

  function _clearError(el) {
    var box = el.querySelector('.wa-error');
    if (box) box.textContent = '';
  }

  // Public API — called from wishlist.js's heart click handlers.
  window.WishlistAssign = { onAdded: onAdded };

})();
